/**
 * estimateSectionPages — TPN-2 (Template Page Numbering, 2026-05-18).
 *
 * Heuristic page-count estimator. Approximates how many printed A4 (or
 * Letter) pages a section consumes based on word/char count, chart count,
 * table row count, and the rendered font size.
 *
 * Not a substitute for measured layout (TPN-5) — use this at edit time
 * so the Studio Pages tab can render "p.4-p.7 (est)" chips without
 * waiting for a paint. Print/PDF export overlays measured values when
 * available; the `source` field on the resolved PageMap entry
 * distinguishes 'estimated' from 'measured'.
 *
 * Accuracy goal: ±10% on typical feasibility reports. Speed goal:
 * <5ms for a 30-section template (each call is O(rows + chars) and
 * the resolver caches the result on `section.estimatedPages`).
 *
 * Backward-compat: when a section has neither body, charts, nor tables
 * (e.g. a fixture missing fields), the estimator returns 1 — a section
 * always consumes at least one page slot.
 */

import type { SectionConfig } from '../../types/reportTemplate';

/**
 * Page-geometry options passed to the estimator. Values are in
 * millimetres / point so the unit chain is intuitive when reading the
 * formulas:
 *
 *   - pageHeightMm   — body height of the page MINUS top/bottom
 *                       margins. A4 portrait at 60px (≈15.9mm) margin
 *                       top/bottom: 297 − 32 ≈ 265 mm. Use the BODY
 *                       height, NOT the full sheet — the estimator
 *                       only counts lines that actually fit in the
 *                       content area.
 *   - pageWidthMm    — body width (same caveat). A4 body ≈ 170 mm at
 *                       70/60 mm side margins.
 *   - fontSize       — body text font size in points (1 pt ≈ 0.353 mm).
 *                       Headings, captions, etc. are folded into the
 *                       chart/table heuristics so they don't need a
 *                       separate fontSize knob.
 *   - charsPerLineOverride — escape hatch for tests / non-default
 *                       column widths. When omitted the estimator
 *                       derives chars-per-line from the page width
 *                       and font size.
 */
export interface EstimatorOptions {
  pageHeightMm: number;
  pageWidthMm: number;
  fontSize: number;
  charsPerLineOverride?: number;
}

/** A4 body geometry — pageHeight/Width minus the 60-70px margins the
 *  viewer applies (`p-[60px] pl-[70px]`). Numbers are intentionally
 *  conservative — the estimator marginally OVER-estimates pages so a
 *  pinned downstream section is less likely to overlap. */
export const DEFAULT_A4: EstimatorOptions = {
  pageHeightMm: 245, // 297mm sheet − 25mm top − 27mm bottom (60/65px @ 96dpi)
  pageWidthMm: 165,  // 210mm sheet − 25mm left − 20mm right (with 10mm rail)
  fontSize: 10,
};

/** Letter (US) body geometry — 8.5" × 11" with similar margins. */
export const DEFAULT_LETTER: EstimatorOptions = {
  pageHeightMm: 230, // 279mm sheet − margins
  pageWidthMm: 170,  // 216mm sheet − margins
  fontSize: 10,
};

/** Visual lines a typical chart consumes (caption + axis labels + plot
 *  area). Calibrated against the SAMPLE_REPORT charts which average
 *  ~280px tall on a 794px-wide preview — at 10pt body = ~14 lines.
 *  Bumped to 20 to be conservative (charts trend taller in long
 *  reports with multi-series legends). */
export const LINES_PER_CHART = 20;

/** Visual lines a typical table ROW consumes — header (1.5) + body (1)
 *  per data row + a final separator. Captured as a per-row coefficient
 *  so callers can pass the raw row count without padding for headers. */
export const LINES_PER_TABLE_ROW = 1.4;

/** Constant header overhead applied per table (caption + thead). */
export const LINES_TABLE_HEADER = 2;

/**
 * TPN-CORE (2026-05-19) — visual lines consumed by the section's H1
 * heading. Calibrated at a 24pt heading with 1.4 leading on a 10pt
 * body: heading-mm = 24/72×25.4 ≈ 8.5mm vs. body-line-mm ≈ 4.94mm →
 * one heading "uses" about 1.7 body lines, plus margin-before of
 * ~12pt and margin-after of ~6pt totalling ~2 more body lines.
 * Round up to 4 to stay slightly over-estimating (matches the
 * estimator-bias rule documented at file head).
 */
export const LINES_PER_HEADING = 4;

/** Line-height multiplier — body text at 1.4 leading is the
 *  industry-standard for printed reports. */
const LINE_HEIGHT_MULTIPLIER = 1.4;

/** 1 point in millimetres — typographic constant. */
const POINTS_PER_MM = 1 / 0.3527777778;

/**
 * Derive characters-per-line from a width-in-mm and a font-size-in-pt.
 * Uses the standard "average glyph width ≈ 0.5 × fontSize" heuristic
 * (proportional fonts: monospaced is closer to 0.6 but body text in
 * the report viewer is proportional). Bottoms out at 40 chars/line so
 * narrow side-rails don't push the estimator to zero divisions.
 *
 * Exported for unit-test parity — the resolver also reuses it.
 */
export function computeCharsPerLine(opts: EstimatorOptions): number {
  if (opts.charsPerLineOverride != null) return opts.charsPerLineOverride;
  const avgGlyphMm = (opts.fontSize * 0.5) / POINTS_PER_MM;
  if (avgGlyphMm <= 0) return 40;
  const chars = opts.pageWidthMm / avgGlyphMm;
  return Math.max(40, Math.round(chars));
}

/**
 * Lines that fit on one page body at the given font size.
 *
 *   linesPerPage = pageHeightMm / (fontSize-mm × lineHeightMultiplier)
 *
 * Bottoms out at 10 lines/page so a degenerate config (huge font,
 * tiny page) doesn't produce a div-by-≈0 page count.
 */
export function computeLinesPerPage(opts: EstimatorOptions): number {
  const fontMm = opts.fontSize / POINTS_PER_MM;
  const lineMm = fontMm * LINE_HEIGHT_MULTIPLIER;
  if (lineMm <= 0) return 10;
  return Math.max(10, opts.pageHeightMm / lineMm);
}

/**
 * Loose body-content shape the estimator reads. We don't import the
 * concrete `SiteRagReport` type here because the estimator runs in
 * Studio fixtures, sample fixtures, and live reports — each carries
 * the body fields under slightly different names. Callers pass a
 * pre-normalised snapshot so the estimator stays pure and trivially
 * testable.
 *
 *   - body            — narrative text (combined paragraphs).
 *   - chartCount      — number of distinct charts the section renders.
 *   - tableRowCount   — sum of rows across all tables in the section.
 *                       Includes the header row; the estimator adds
 *                       the `LINES_TABLE_HEADER` constant on top.
 *   - tableCount      — number of distinct tables (for header overhead).
 *   - heading         — TPN-CORE (2026-05-19) — H1 / section title text.
 *                       Used to charge the section a heading-line
 *                       budget (`LINES_PER_HEADING`); the heading's
 *                       own character count is folded in too because
 *                       long titles can wrap onto a second line.
 *   - pageBreakBefore — TPN-CORE — when true the section is treated as
 *                       starting on a fresh page even if it would
 *                       otherwise fit on the prior page. Implemented
 *                       as a heading-line surcharge so the estimator
 *                       stays a pure function of one section at a
 *                       time; the resolver enforces the actual break
 *                       via `startMode` (the two flags compose).
 */
export interface EstimableSection {
  body?: string;
  chartCount?: number;
  tableCount?: number;
  tableRowCount?: number;
  heading?: string;
  pageBreakBefore?: boolean;
}

/**
 * TPN-CORE (2026-05-19) — pure helper that returns the total
 * character-equivalent count for a section's content. Used internally
 * by `estimateSectionPages` and exported so caller tooling
 * (verifier diagnostics, page-budget warnings, DOCX exporter) can
 * read a single scalar for "how dense is this section?" without
 * recomputing.
 *
 * Char-equivalents are computed as:
 *   - body.length                                  (real chars)
 *   - heading.length × HEADING_CHAR_WEIGHT         (titles bias bigger)
 *   - chartCount   × CHART_CHAR_EQUIVALENT         (each chart ≈ a paragraph)
 *   - tableRowCount × TABLE_ROW_CHAR_EQUIVALENT    (each row ≈ a short line)
 *
 * `Number.isFinite()` guards every numeric field so a stray `NaN` /
 * `Infinity` from a malformed report fixture can't poison downstream
 * page math. Negative inputs are clamped to zero (defensive against
 * upstream JSON corruption).
 */
export const HEADING_CHAR_WEIGHT = 1.4;
export const CHART_CHAR_EQUIVALENT = 400;
export const TABLE_ROW_CHAR_EQUIVALENT = 60;

export function estimateSectionContentChars(section: EstimableSection): number {
  const bodyLen = typeof section.body === 'string' ? section.body.length : 0;
  const headingLen = typeof section.heading === 'string' ? section.heading.length : 0;
  const rawChartCount = section.chartCount ?? 0;
  const rawTableRows = section.tableRowCount ?? 0;

  const chartCount = Number.isFinite(rawChartCount) ? Math.max(0, rawChartCount) : 0;
  const tableRows = Number.isFinite(rawTableRows) ? Math.max(0, rawTableRows) : 0;
  const safeBody = Number.isFinite(bodyLen) ? Math.max(0, bodyLen) : 0;
  const safeHeading = Number.isFinite(headingLen) ? Math.max(0, headingLen) : 0;

  const total =
    safeBody +
    safeHeading * HEADING_CHAR_WEIGHT +
    chartCount * CHART_CHAR_EQUIVALENT +
    tableRows * TABLE_ROW_CHAR_EQUIVALENT;

  return Number.isFinite(total) ? Math.max(0, Math.round(total)) : 0;
}

/**
 * Estimate how many printed pages `section` consumes.
 *
 * Returns at LEAST 1 — a registered section always claims at least
 * one page slot in the page-map; zero would let downstream sections
 * collapse onto its start page, which violates the resolver's
 * monotonic-cursor invariant.
 *
 * Accounting (TPN-CORE 2026-05-19 expansion):
 *   - body lines            = bodyChars / charsPerLine
 *   - chart lines           = chartCount × LINES_PER_CHART
 *   - table lines           = tableCount × LINES_TABLE_HEADER
 *                              + tableRowCount × LINES_PER_TABLE_ROW
 *   - heading lines         = LINES_PER_HEADING (always charged) +
 *                              max(0, headingLen − charsPerLine) wrap.
 *   - pageBreakBefore tax   = when true, the section is treated as
 *                              starting on a fresh page; we add an
 *                              extra LINES_PER_HEADING budget so the
 *                              ceil rounds up cleanly. Resolver-side
 *                              `startMode` does the actual break;
 *                              this surcharge keeps the page math
 *                              honest if the caller only sets the
 *                              flag.
 */
export function estimateSectionPages(
  section: EstimableSection,
  options: EstimatorOptions = DEFAULT_A4,
): number {
  const charsPerLine = computeCharsPerLine(options);
  const linesPerPage = computeLinesPerPage(options);

  const rawBodyLen = typeof section.body === 'string' ? section.body.length : 0;
  const bodyChars = Number.isFinite(rawBodyLen) ? Math.max(0, rawBodyLen) : 0;
  const bodyLines = charsPerLine > 0 ? bodyChars / charsPerLine : 0;

  const rawChartCount = section.chartCount ?? 0;
  const chartCount = Number.isFinite(rawChartCount) ? Math.max(0, rawChartCount) : 0;
  const chartLines = chartCount * LINES_PER_CHART;

  const rawTableCount = section.tableCount ?? 0;
  const rawTableRows = section.tableRowCount ?? 0;
  const tableCount = Number.isFinite(rawTableCount) ? Math.max(0, rawTableCount) : 0;
  const tableRows = Number.isFinite(rawTableRows) ? Math.max(0, rawTableRows) : 0;
  const tableLines = tableCount * LINES_TABLE_HEADER + tableRows * LINES_PER_TABLE_ROW;

  // TPN-CORE — heading height accounting. Every section claims the
  // heading budget; an unusually long title (>1 line) adds wrap lines.
  const rawHeadingLen = typeof section.heading === 'string' ? section.heading.length : 0;
  const headingLen = Number.isFinite(rawHeadingLen) ? Math.max(0, rawHeadingLen) : 0;
  const headingWrapLines = charsPerLine > 0 ? Math.max(0, headingLen - charsPerLine) / charsPerLine : 0;
  const headingLines = LINES_PER_HEADING + headingWrapLines;

  // TPN-CORE — pageBreakBefore surcharge. Costs another heading
  // budget to bias the ceil toward "starts on a fresh page".
  const pageBreakSurcharge = section.pageBreakBefore === true ? LINES_PER_HEADING : 0;

  const totalLines = headingLines + bodyLines + chartLines + tableLines + pageBreakSurcharge;
  const safeTotal = Number.isFinite(totalLines) ? Math.max(0, totalLines) : 0;
  const pages = linesPerPage > 0 ? Math.ceil(safeTotal / linesPerPage) : 1;
  return Math.max(1, Number.isFinite(pages) ? pages : 1);
}

/**
 * Pull an `EstimableSection` snapshot out of a SectionConfig + an
 * optional resolved-content callback. The Studio doesn't know the
 * actual narrative text yet (templates carry layout, not content), so
 * the default heuristic is a sensible canonical-section preset baked
 * into the registry (`reportSections.ts`). Live reports pass the
 * `getContent` resolver to feed real word counts in.
 *
 * Exported separately so call sites can pre-compute snapshots without
 * importing the full estimator surface.
 */
export function snapshotSection(
  s: Pick<SectionConfig, 'sectionId'>,
  getContent?: (id: string) => EstimableSection | undefined,
): EstimableSection {
  return getContent?.(s.sectionId) ?? DEFAULT_CANONICAL_SNAPSHOT;
}

/** Average canonical-section snapshot — used when the caller doesn't
 *  supply a content resolver. Calibrated against a typical feasibility
 *  section (~500-700 words of narrative + 1 chart + 1 table with 6
 *  rows): roughly 1.5 pages. The estimator returns ≥1 so this snapshot
 *  produces 1-2 pages depending on geometry. */
const DEFAULT_CANONICAL_SNAPSHOT: EstimableSection = {
  body: 'x'.repeat(3000),
  chartCount: 1,
  tableCount: 1,
  tableRowCount: 6,
};
